import React, { useRef, useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Mic, Square, Loader2, Play, Pause, Trash2, Check, Sparkles, AlertTriangle, Pill, Target, ShieldAlert,
} from "lucide-react";

const KEYWORDS = {
  medication: /\b(medication|medicine|meds?|tablet|pill|prn|dose|antibiotic|paracetamol|insulin|inhaler)\b/gi,
  outcome: /\b(happy|cheerful|calm|content|smiled|engaged|independent|completed|achieved|enjoyed|participated|walked|shopping|walked|went out)\b/gi,
  safeguarding: /\b(bruise|injury|hit|slap|shout|scream|threat|abuse|neglect|missing|fell|fall|choked|hurt|blood|unresponsive|refuse)\b/gi,
};

/**
 * Voice-to-Care Notes.
 *
 * Records the carer's voice with MediaRecorder, uploads to
 * POST /ai/care-notes/transcribe which returns transcript + CQC-rewritten note.
 *
 * Props:
 *   clientId   optional — enriches context
 *   onApply(rewritten, transcript) — approved text applied to the caller
 *   relatedId  optional — daily-log id for storing the recording alongside
 */
export default function VoiceCareRecorder({ clientId, onApply, relatedId }) {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [rewritten, setRewritten] = useState("");
  const [storeRecording, setStoreRecording] = useState(true);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setSupported(false);
    }
    return () => stopStream();
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const start = async () => {
    setTranscript("");
    setRewritten("");
    setAudioUrl(null);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setAudioUrl(URL.createObjectURL(blob));
        stopStream();
        await upload(blob);
      };
      mr.start(500);
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      setRecording(false);
      mediaRef.current.stop();
    }
  };

  const upload = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice-note.webm");
      if (clientId) fd.append("client_id", clientId);
      fd.append("auto_rewrite", "true");
      fd.append("store_recording", storeRecording ? "true" : "false");
      if (relatedId) fd.append("related_id", relatedId);
      const r = await api.post("/ai/care-notes/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript(r.data.transcript || "");
      setRewritten(r.data.rewritten || "");
      if (!r.data.transcript) toast.error("No speech detected — try again");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const discard = () => {
    setAudioUrl(null); setTranscript(""); setRewritten(""); setSeconds(0);
  };

  const approve = () => {
    if (!rewritten && !transcript) return;
    onApply?.(rewritten || transcript, transcript);
    toast.success("Voice note applied — review and save");
    discard();
  };

  const mmss = `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;

  if (!supported) {
    return (
      <div className="text-xs text-slate-500 italic border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2" data-testid="voice-unsupported">
        Voice recording not supported in this browser.
      </div>
    );
  }

  return (
    <div className="border border-violet-200 dark:border-violet-800 bg-violet-50/30 dark:bg-violet-950/20 rounded-md p-3 space-y-3" data-testid="voice-care-recorder">
      <div className="flex items-center gap-2 flex-wrap">
        {!recording && !uploading && (
          <Button type="button" onClick={start} data-testid="btn-voice-record" className="bg-red-600 hover:bg-red-700 text-white h-11 md:h-9">
            <Mic className="w-4 h-4 mr-1.5" /> Record voice note
          </Button>
        )}
        {recording && (
          <Button type="button" onClick={stop} data-testid="btn-voice-stop" className="bg-red-600 hover:bg-red-700 text-white h-11 md:h-9 animate-pulse">
            <Square className="w-4 h-4 mr-1.5" /> Stop · {mmss}
          </Button>
        )}
        {uploading && (
          <Button type="button" disabled className="h-11 md:h-9" data-testid="voice-uploading">
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Transcribing with Whisper…
          </Button>
        )}
        {(audioUrl || transcript) && !recording && !uploading && (
          <Button type="button" variant="outline" onClick={discard} data-testid="btn-voice-discard" className="h-11 md:h-9 text-slate-600">
            <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Discard
          </Button>
        )}
        <div className="flex-1" />
        <label className="text-[11px] text-slate-600 dark:text-slate-300 inline-flex items-center gap-1">
          <input type="checkbox" checked={storeRecording} onChange={(e) => setStoreRecording(e.target.checked)} data-testid="voice-store-toggle" />
          Save recording
        </label>
      </div>

      {audioUrl && (
        <audio controls src={audioUrl} className="w-full h-8" data-testid="voice-playback" />
      )}

      {transcript && (
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Raw transcript (editable)</label>
          <Textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={3}
            data-testid="voice-transcript"
            className="text-sm bg-white dark:bg-slate-900"
          />
        </div>
      )}

      {rewritten && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] uppercase tracking-widest text-violet-700 dark:text-violet-300 font-semibold flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI CQC-compliant note (editable)
            </label>
            <HighlightLegend />
          </div>
          <Textarea
            value={rewritten}
            onChange={(e) => setRewritten(e.target.value)}
            rows={8}
            data-testid="voice-rewritten"
            className="text-sm bg-white dark:bg-slate-900 font-mono"
          />
          <HighlightedPreview text={rewritten} />
          <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800">
            ⚠️ Manager review required. Read the AI note carefully — edit or discard if any detail was misheard.
          </div>
        </div>
      )}

      {(transcript || rewritten) && !uploading && (
        <div className="flex items-center justify-end gap-2">
          <Button onClick={approve} data-testid="btn-voice-approve" className="bg-emerald-700 hover:bg-emerald-800 text-white">
            <Check className="w-4 h-4 mr-1.5" /> Approve & apply to note
          </Button>
        </div>
      )}
    </div>
  );
}

function HighlightLegend() {
  return (
    <div className="hidden md:flex items-center gap-2 text-[10px]">
      <span className="inline-flex items-center gap-0.5 text-sky-700"><Pill className="w-3 h-3" /> medication</span>
      <span className="inline-flex items-center gap-0.5 text-emerald-700"><Target className="w-3 h-3" /> outcome</span>
      <span className="inline-flex items-center gap-0.5 text-red-700"><ShieldAlert className="w-3 h-3" /> safeguarding</span>
    </div>
  );
}

function HighlightedPreview({ text }) {
  if (!text) return null;
  const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = escapeHtml(text);
  html = html.replace(KEYWORDS.medication, (m) => `<mark class="bg-sky-100 text-sky-800 rounded px-1">${m}</mark>`);
  html = html.replace(KEYWORDS.outcome, (m) => `<mark class="bg-emerald-100 text-emerald-800 rounded px-1">${m}</mark>`);
  html = html.replace(KEYWORDS.safeguarding, (m) => `<mark class="bg-red-100 text-red-800 rounded px-1">${m}</mark>`);
  html = html.replace(/\n/g, "<br />");
  return (
    <div
      className="mt-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
      data-testid="voice-highlighted-preview"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
